import type { DataSourceId, DataSourceProvider } from '../../contexts/DataSourceContext';
import { getWelcomeSourceDescriptor } from './source-catalog';

export const WELCOME_RESUME_INTENT_STORAGE_KEY = 'ownyou.welcome.resumeIntent';
export const WELCOME_RESUME_INTENT_SCHEMA_VERSION = 1;
export const WELCOME_RESUME_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

export type WelcomeResumeIntentStatus = 'pending' | 'returned' | 'failed';

export interface WelcomeResumeIntent {
  schemaVersion: 1;
  sourceId: DataSourceId;
  provider: DataSourceProvider;
  correlationId: string;
  routeId: 'welcome' | 'onboarding';
  returnRoute: '/welcome';
  status: WelcomeResumeIntentStatus;
  startedAt: number;
  updatedAt: number;
  reasonCode?: string;
}

export type WelcomeResumeIntentResolution =
  | { ok: true; intent: WelcomeResumeIntent }
  | { ok: false; reasonCode: 'missing_intent' | 'invalid_intent' | 'stale_intent' | 'source_mismatch' | 'state_mismatch' };

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const FORBIDDEN_INTENT_PATTERNS = [
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /client[_-]?secret/i,
  /authorization/i,
  /bearer\s+/i,
  /providerRawResponse/i,
  /raw[_-]?(body|content|response|source)/i,
  /sourceBody/i,
  /prompt/i,
  /credential/i,
  /private[_-]?key/i,
] as const;

function defaultCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function defaultStorage(): StorageLike {
  return sessionStorage;
}

function isWelcomeResumeIntent(value: unknown): value is WelcomeResumeIntent {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.schemaVersion === WELCOME_RESUME_INTENT_SCHEMA_VERSION
    && typeof record.sourceId === 'string'
    && typeof record.provider === 'string'
    && typeof record.correlationId === 'string'
    && (record.routeId === 'welcome' || record.routeId === 'onboarding')
    && record.returnRoute === '/welcome'
    && (record.status === 'pending' || record.status === 'returned' || record.status === 'failed')
    && typeof record.startedAt === 'number'
    && typeof record.updatedAt === 'number';
}

export function validateWelcomeResumeIntentBodySafety(intent: WelcomeResumeIntent): string[] {
  const serialized = JSON.stringify(intent);
  return FORBIDDEN_INTENT_PATTERNS
    .filter(pattern => pattern.test(serialized))
    .map(pattern => pattern.source);
}

export function createWelcomeResumeIntent(input: {
  sourceId: DataSourceId;
  provider?: DataSourceProvider;
  routeId?: 'welcome' | 'onboarding';
  correlationId?: string;
  now?: number;
}): WelcomeResumeIntent {
  const descriptor = getWelcomeSourceDescriptor(input.sourceId);
  const now = input.now ?? Date.now();
  const intent: WelcomeResumeIntent = {
    schemaVersion: WELCOME_RESUME_INTENT_SCHEMA_VERSION,
    sourceId: input.sourceId,
    provider: input.provider ?? descriptor.provider,
    correlationId: input.correlationId ?? defaultCorrelationId(),
    routeId: input.routeId ?? 'welcome',
    returnRoute: '/welcome',
    status: 'pending',
    startedAt: now,
    updatedAt: now,
  };

  const violations = validateWelcomeResumeIntentBodySafety(intent);
  if (violations.length > 0) {
    throw new Error(`Welcome resume intent body-safety violation: ${violations.join(', ')}`);
  }

  return intent;
}

export function writeWelcomeResumeIntent(intent: WelcomeResumeIntent, storage: StorageLike = defaultStorage()): void {
  const violations = validateWelcomeResumeIntentBodySafety(intent);
  if (violations.length > 0) {
    throw new Error(`Welcome resume intent body-safety violation: ${violations.join(', ')}`);
  }
  storage.setItem(WELCOME_RESUME_INTENT_STORAGE_KEY, JSON.stringify(intent));
}

export const saveWelcomeResumeIntent = writeWelcomeResumeIntent;

export function readWelcomeResumeIntent(storage: StorageLike = defaultStorage()): WelcomeResumeIntent | null {
  const raw = storage.getItem(WELCOME_RESUME_INTENT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isWelcomeResumeIntent(parsed)) return null;
    if (validateWelcomeResumeIntentBodySafety(parsed).length > 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function resolveWelcomeResumeIntent(input: {
  storage?: StorageLike;
  sourceId?: DataSourceId;
  correlationId?: string;
  now?: number;
  maxAgeMs?: number;
} = {}): WelcomeResumeIntentResolution {
  const storage = input.storage ?? defaultStorage();
  const raw = storage.getItem(WELCOME_RESUME_INTENT_STORAGE_KEY);
  const intent = readWelcomeResumeIntent(storage);

  if (!raw) return { ok: false, reasonCode: 'missing_intent' };
  if (!intent) {
    storage.removeItem(WELCOME_RESUME_INTENT_STORAGE_KEY);
    return { ok: false, reasonCode: 'invalid_intent' };
  }

  const now = input.now ?? Date.now();
  if (now - intent.startedAt > (input.maxAgeMs ?? WELCOME_RESUME_INTENT_MAX_AGE_MS)) {
    storage.removeItem(WELCOME_RESUME_INTENT_STORAGE_KEY);
    return { ok: false, reasonCode: 'stale_intent' };
  }

  if (input.sourceId && input.sourceId !== intent.sourceId) {
    return { ok: false, reasonCode: 'source_mismatch' };
  }

  if (input.correlationId && input.correlationId !== intent.correlationId) {
    return { ok: false, reasonCode: 'state_mismatch' };
  }

  return { ok: true, intent };
}

export function finalizeWelcomeResumeIntent(input: {
  storage?: StorageLike;
  status: Extract<WelcomeResumeIntentStatus, 'returned' | 'failed'>;
  reasonCode?: string;
  now?: number;
}): WelcomeResumeIntent | null {
  const storage = input.storage ?? defaultStorage();
  const intent = readWelcomeResumeIntent(storage);
  if (!intent) return null;

  const finalized: WelcomeResumeIntent = {
    ...intent,
    status: input.status,
    ...(input.reasonCode ? { reasonCode: input.reasonCode } : {}),
    updatedAt: input.now ?? Date.now(),
  };
  writeWelcomeResumeIntent(finalized, storage);
  return finalized;
}

export function clearWelcomeResumeIntent(storage: StorageLike = defaultStorage()): void {
  storage.removeItem(WELCOME_RESUME_INTENT_STORAGE_KEY);
}
